import React, { useState, useRef, useEffect, useCallback } from 'react'
import ResizeObserver from 'resize-observer-polyfill'
import { debounce } from 'lodash'
import { Typography, Popper, makeStyles } from '@material-ui/core'
import PictoIcon from './PictoIcon'

const getColor = (theme, value) => {
  switch (value) {
    case 2:
      return theme.palette.common.green
    case 1:
      return theme.palette.common.yellow
    case 0:
      return theme.palette.common.red
    default:
      return theme.palette.common.white
  }
}

const useStyles = makeStyles((theme) => ({
  root: ({ disabled }) => ({
    opacity: disabled ? 0.2 : 1,
    '&:hover': {
      boxShadow: 'unset',
    },
  }),
  container: ({ sideLength }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: sideLength,
  }),
  topContainer: {
    border: '2px solid black',
    borderBottom: '1px solid black',
    backgroundColor: 'white',
  },
  bottomContainer: {
    border: '2px solid black',
    borderTop: '1px solid black',
    backgroundColor: ({ value }) => getColor(theme, value),
  },
  icon: ({ sideLength }) => ({
    fontSize: 0.55 * sideLength,
    color: theme.palette.common.black,
  }),
  value: ({ sideLength }) => ({
    fontSize: 0.5 * sideLength,
    fontWeight: 800,
    color: theme.palette.common.black,
  }),
  popper: {
    zIndex: theme.zIndex.tooltip,
    pointerEvents: 'none',
  },
  tooltip: {
    maxWidth: 300,
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: 'rgba(97, 97, 97, 0.9)',
    color: theme.palette.common.white,
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.8rem',
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.4,
    wordWrap: 'break-word',
  },
  tooltipHidden: {
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}))

const Domino = ({
  value,
  handicap,
  disabled,
  className = '',
  tabIndex = 0,
  tooltip = '',
}) => {
  const [sideLength, setSideLength] = useState(0)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [anchorElement, setAnchorElement] = useState(null)
  const rootRef = useRef(null)
  const tooltipId = `domino-tooltip-${handicap}`
  const setRootRef = useCallback((node) => {
    rootRef.current = node
    setAnchorElement(node)
  }, [])
  const classes = useStyles({
    sideLength,
    value,
    disabled,
  })

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && tooltipOpen) {
      setTooltipOpen(false)
    }
  }

  const getLabel = () => {
    switch (value) {
      case 2:
        return 'A'
      case 1:
        return 'B'
      case 0:
        return 'C'
      default:
        return ''
    }
  }

  useEffect(() => {
    let resizeObserver = null
    if (rootRef) {
      resizeObserver = new ResizeObserver(
        debounce((entries) => {
          setSideLength(entries[0].contentRect.width)
        }, 200),
      )

      resizeObserver.observe(rootRef.current)
    }
    return () => resizeObserver.disconnect()
  }, [])

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={setRootRef}
        className={`${classes.root} ${className}`}
        tabIndex={tabIndex}
        aria-describedby={tooltipId}
        role="img"
        onKeyDown={handleKeyDown}
        onFocus={() => setTooltipOpen(true)}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onBlur={() => setTooltipOpen(false)}
      >
        <div
          className={`${classes.container} ${classes.topContainer}`}
          aria-hidden="true"
        >
          <PictoIcon gutterBottom={false} className={classes.icon}>
            {handicap}
          </PictoIcon>
        </div>
        <div
          className={`${classes.container} ${classes.bottomContainer}`}
          aria-hidden="true"
        >
          <Typography gutterBottom={false} className={classes.value}>
            {getLabel()}
          </Typography>
        </div>
      </div>
      <Popper
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        placement="left"
        className={classes.popper}
      >
        <div
          id={tooltipId}
          role="tooltip"
          className={`${classes.tooltip} ${
            tooltipOpen ? '' : classes.tooltipHidden
          }`}
        >
          {tooltip}
        </div>
      </Popper>
    </>
  )
}

export default Domino
